import { StatusCodes } from "http-status-codes";
import AuthService from "@/modules/Auth/services/auth.service";
import { AUTH_EVENTS, authBus } from "@/core/eventBusses/auth";
import {
  ChangePasswordDto,
  ForgotPasswordDto,
  LoginDto,
  SignupDto,
  ResetPasswordDto,
  VerifyLoginAuthenticatorDto,
} from "@/modules/Auth/dtos/auth.dto";
import { ActionTree } from "vuex";
import { IAuthState } from "@/core/interfaces/authState.interface";
import { ICoreErrorResponse } from "@/core/interfaces/coreResponse.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import analyticsService from "@/analytic.service";
import { StoreState } from "@/store/type";
import UTILS from "@/core/utils";
import { LoginResponse } from "../types";
import { AUTH_MUTATIONS } from "./mutations";
import { AUTH_ANALYTICS_EVENTS } from "../constants/analyticsEvents";

export enum AUTH_ACTIONS {
  /**
   * This action handles user login.
   * @param context - The action context.
   * @param {LoginDto} payload - The login data transfer object.
   * @example
   * // Dispatch the action
   * this.$store.dispatch(AUTH_ACTIONS.LOGIN, loginDto);
   */
  LOGIN = "LOGIN",

  /**
   * This action handles user signup.
   * @param context - The action context.
   * @param {Object} payload - The signup payload and callback.
   * @param {SignupDto} payload.payload - The signup data transfer object.
   * @param {Function} payload.callback - The callback function to execute after signup.
   * @example
   * // Dispatch the action
   * this.$store.dispatch(AUTH_ACTIONS.SIGNUP, { payload: signupDto, callback });
   */
  SIGNUP = "SIGNUP",

  /**
   * This action handles forgot password process.
   * @param context - The action context.
   * @param {ForgotPasswordDto} payload - The forgot password data transfer object.
   * @example
   * // Dispatch the action
   * this.$store.dispatch(AUTH_ACTIONS.FORGOT_PASSWORD, forgotPasswordDto);
   */
  FORGOT_PASSWORD = "FORGOT_PASSWORD",

  /**
   * This action handles reset password process.
   * @param context - The action context.
   * @param {ResetPasswordDto} payload - The reset password data transfer object.
   * @example
   * // Dispatch the action
   * this.$store.dispatch(AUTH_ACTIONS.RESET_PASSWORD, resetPasswordDto);
   */
  RESET_PASSWORD = "RESET_PASSWORD",

  /**
   * This action handles change password process.
   * @param context - The action context.
   * @param {ChangePasswordDto} payload - The change password data transfer object.
   * @example
   * // Dispatch the action
   * this.$store.dispatch(AUTH_ACTIONS.CHANGE_PASSWORD, changePasswordDto);
   */
  CHANGE_PASSWORD = "CHANGE_PASSWORD",

  /**
   * This action verifies authenticator code for login (2FA).
   */
  VERIFY_AUTHENTICATOR_LOGIN = "VERIFY_AUTHENTICATOR_LOGIN",
}

const actions: ActionTree<IAuthState, StoreState> = {
  async [AUTH_ACTIONS.LOGIN](context, payload: LoginDto): Promise<void> {
    try {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, true);

      const authService = new AuthService();
      const response = await authService.login(payload);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data
      ) {
        const data: LoginResponse = response.data;

        if (data.is_authorized === true && data.multi_factor_token) {
          context.commit(AUTH_MUTATIONS.SET_TWO_FACTOR_LOGIN_STATE, {
            token: data.multi_factor_token,
            required: true,
            qrCode: data.qr_code ?? null,
          });

          UTILS.notify(
            response.message || "Submit OTP to Login",
            NOTIFICATION_TYPES.INFO,
          );
        } else {
          context.commit(AUTH_MUTATIONS.SAVE_LOGIN_RESPONSE, data);
          context.commit(AUTH_MUTATIONS.SET_TWO_FACTOR_LOGIN_STATE, {
            token: null,
            required: false,
          });

          authBus.emit(AUTH_EVENTS.LOGIN, data);

          UTILS.notify("Successfully Logged In", NOTIFICATION_TYPES.SUCCESS);
        }
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.error || exception.message,
        NOTIFICATION_TYPES.ERROR,
      );
    } finally {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, false);
    }
  },

  async [AUTH_ACTIONS.SIGNUP](
    context,
    { payload, callback }: { payload: SignupDto; callback: () => void },
  ) {
    context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, true);

    try {
      const authService = new AuthService();

      const response = await authService.signup(payload);

      if (response.success && response.status === StatusCodes.OK) {
        analyticsService.logActionEvent(AUTH_ANALYTICS_EVENTS.SIGNUP, payload);
        callback();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.error || exception.message,
        NOTIFICATION_TYPES.ERROR,
      );
    } finally {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, false);
    }
  },

  async [AUTH_ACTIONS.FORGOT_PASSWORD](
    context,
    payload: ForgotPasswordDto,
  ): Promise<void> {
    try {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, true);

      const authService = new AuthService();
      const response = await authService.forgotPassword(payload);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data
      ) {
        const message = `Change Password link sent successfully to ${payload.email}`;

        UTILS.notify(message, NOTIFICATION_TYPES.SUCCESS);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.error || exception.message,
        NOTIFICATION_TYPES.ERROR,
      );
    } finally {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, false);
    }
  },

  async [AUTH_ACTIONS.RESET_PASSWORD](
    context,
    payload: ResetPasswordDto,
  ): Promise<void> {
    try {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_RESETTING_PASSWORD, true);

      const authService = new AuthService();
      const response = await authService.resetPassword(payload);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data
      ) {
        const message = "Password Reseted Successfully";

        UTILS.notify(message, NOTIFICATION_TYPES.SUCCESS);

        window.location.replace("/");
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.error || exception.message,
        NOTIFICATION_TYPES.ERROR,
      );
    } finally {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_RESETTING_PASSWORD, false);
    }
  },

  async [AUTH_ACTIONS.CHANGE_PASSWORD](_, payload: ChangePasswordDto) {
    try {
      const authService = new AuthService();
      const response = await authService.changePassword(payload);

      if (response.success) {
        UTILS.notify(
          NOTIFICATION_MESSAGES.RESET_PASSWORD_SUCCESS,
          NOTIFICATION_TYPES.SUCCESS,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.message || exception.error,
        NOTIFICATION_TYPES.ERROR,
      );
    }
  },

  async [AUTH_ACTIONS.VERIFY_AUTHENTICATOR_LOGIN](
    context,
    payload: VerifyLoginAuthenticatorDto,
  ) {
    try {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, true);

      const authService = new AuthService();
      const body: VerifyLoginAuthenticatorDto = {
        multi_factor_token: context.state.twoFactorToken || "",
        otp_code: payload.otp_code,
        is_login: true,
      };

      const response = await authService.verifyAuthenticator(body);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data
      ) {
        const data: LoginResponse = response.data;

        context.commit(AUTH_MUTATIONS.SAVE_LOGIN_RESPONSE, data);
        context.commit(AUTH_MUTATIONS.SET_TWO_FACTOR_LOGIN_STATE, {
          token: null,
          required: false,
        });

        authBus.emit(AUTH_EVENTS.LOGIN, data);

        UTILS.notify("Successfully Logged In", NOTIFICATION_TYPES.SUCCESS);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      UTILS.notify(
        exception.error || exception.message,
        NOTIFICATION_TYPES.ERROR,
      );
    } finally {
      context.commit(AUTH_MUTATIONS.TOGGLE_IS_AUTHENTICATING, false);
    }
  },
};

export default actions;
